import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  MoneyInput,
  Textarea,
  toast,
} from '@mlm/ui';
import { AR_PERMS, TAX_CODES } from '@mlm/shared';
import {
  createArInvoice,
  deleteArInvoiceDraft,
  getArInvoice,
  listBranches,
  listCustomers,
  listGlAccounts,
  listPaymentTerms,
  postArInvoice,
  updateArInvoice,
  voidArInvoice,
  type ArInvoiceLineInput,
} from '../../lib/api';
import { useHasPerm } from '../../lib/auth-store';
import { ProductPicker } from '../../components/product-picker';

/**
 * The general-billing editor (R2 Phase C), modelled on `bill-editor.tsx`.
 *
 * A billing is a draft until it is posted, and everything about it is editable
 * up to that moment and frozen after. The dialog therefore has two shapes: an
 * editable form for a draft, and a read-only summary plus Void/Print for one
 * that has been issued.
 */

// Display helpers are duplicated from the sibling editors on purpose:
// exporting non-components from a page module breaks Fast Refresh.
function peso(v: string | number | null | undefined): string {
  const n = Number(v ?? 0);
  return `₱${(Number.isFinite(n) ? n : 0).toLocaleString('en-PH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function shortDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

/** Local-timezone YYYY-MM-DD for `<input type="date">`. */
function toDateInput(iso?: string | null): string {
  const d = iso ? new Date(iso) : new Date();
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-CA');
}

function sanitizeMoney(v: string): string {
  return v.replace(/,/g, '').trim();
}

/**
 * Server money arrives as 4dp strings ("500.0000"). Seed editable fields with
 * the VALUE untouched — only trailing zeros trimmed — never `toFixed(2)`,
 * which would silently reprice a re-saved draft.
 */
function trimMoney(v: string | null | undefined): string {
  if (v == null || v === '') return '';
  const s = String(v);
  if (!s.includes('.')) return s;
  return s.replace(/0+$/, '').replace(/\.$/, '');
}

function errText(error: unknown): string | undefined {
  return (error as { response?: { data?: { error?: string } } } | null)?.response?.data?.error;
}

const ERROR_COPY: Record<string, string> = {
  ar_invoicing_disabled: 'General billing is switched off for this tenant.',
  credit_limit_exceeded: 'This would take the customer past their credit limit.',
  invoice_not_draft: 'Only draft billings can be edited, posted or deleted.',
  invoice_draft: 'Drafts are deleted, not voided.',
  invoice_has_payments: 'This billing has payments against it — reverse those first.',
  already_void: 'This billing is already void.',
  draft_has_no_lines: 'Add at least one line before posting.',
  invalid_line: 'A line needs either a product or a description.',
  invalid_qty: 'Quantities must be whole numbers above zero.',
  invalid_amount: 'The billing total must be greater than zero.',
  invalid_discount: 'The discount cannot exceed the subtotal.',
  invalid_unit_price: 'A unit price cannot be negative.',
  income_account_invalid: 'Pick an active income account for that line.',
  unknown_terms_code: 'That payment term no longer exists.',
  branch_required: 'Choose the branch to relieve stock from.',
  branch_not_found: 'That branch no longer exists.',
  product_not_found: 'A product on this billing no longer exists.',
  validation_error: 'A field has an invalid value — check quantities and amounts.',
};

function friendly(error: unknown, fallback: string): string {
  const code = errText(error);
  if (!code) return fallback;
  if (code.startsWith('out_of_stock')) {
    return 'Not enough stock at that branch to relieve this billing.';
  }
  return ERROR_COPY[code] ?? code;
}

const SELECT_CLS = 'h-9 rounded-md border border-input bg-background px-2 text-sm';

const TAX_LABELS: Record<string, string> = {
  vatable: 'Vatable',
  exempt: 'VAT-exempt',
  zero_rated: 'Zero-rated',
};

/** A line is a catalog product XOR a free-text charge. */
type ProductLine = {
  key: string;
  kind: 'product';
  productId: string;
  variantId: string | null;
  label: string;
  qty: string;
  unitPrice: string;
  taxCode: string;
  incomeAccountCode: string;
};
type FreeTextLine = {
  key: string;
  kind: 'freetext';
  description: string;
  qty: string;
  unitPrice: string;
  taxCode: string;
  incomeAccountCode: string;
};
type EditorLine = ProductLine | FreeTextLine;

let keySeq = 0;
const nextKey = () => `l${++keySeq}`;

function lineAmount(l: EditorLine): number {
  return (Number(l.qty) || 0) * (Number(sanitizeMoney(l.unitPrice)) || 0);
}

function toInput(l: EditorLine): ArInvoiceLineInput {
  const shared = {
    qty: Number(l.qty) || 0,
    unitPrice: sanitizeMoney(l.unitPrice),
    taxCode: l.taxCode || null,
    incomeAccountCode: l.incomeAccountCode || null,
  };
  return l.kind === 'product'
    ? { ...shared, productId: l.productId, variantId: l.variantId }
    : { ...shared, description: l.description };
}

export function ArInvoiceEditorDialog({
  invoiceId,
  onClose,
}: {
  /** null = a brand-new draft. */
  invoiceId: string | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const canWrite = useHasPerm(AR_PERMS.invoiceWrite);
  const canVoid = useHasPerm(AR_PERMS.void);

  const detailQ = useQuery({
    queryKey: ['ar-invoice', invoiceId],
    queryFn: () => getArInvoice(invoiceId!),
    enabled: Boolean(invoiceId),
  });
  const customersQ = useQuery({
    queryKey: ['customers', 'picker'],
    queryFn: () => listCustomers({ active: true }),
  });
  const termsQ = useQuery({ queryKey: ['payment-terms'], queryFn: () => listPaymentTerms() });
  const branchesQ = useQuery({ queryKey: ['branches'], queryFn: () => listBranches() });
  const accountsQ = useQuery({ queryKey: ['gl-accounts'], queryFn: listGlAccounts });
  const incomeAccounts = (accountsQ.data?.rows ?? []).filter(
    (a) => a.active && a.type === 'income',
  );

  const [customerId, setCustomerId] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(toDateInput());
  const [termsCode, setTermsCode] = useState('');
  const [reference, setReference] = useState('');
  const [memo, setMemo] = useState('');
  const [discount, setDiscount] = useState('');
  const [deductsInventory, setDeductsInventory] = useState(false);
  const [branchId, setBranchId] = useState('');
  const [lines, setLines] = useState<EditorLine[]>([]);
  const [voiding, setVoiding] = useState(false);
  const [voidReason, setVoidReason] = useState('');

  const invoice = detailQ.data?.invoice;
  const isDraft = !invoiceId || invoice?.status === 'draft';
  const isVoid = invoice?.status === 'void';

  // Seed the form from the loaded draft, once per invoice.
  useEffect(() => {
    if (!detailQ.data) return;
    const { invoice: inv, lines: rows } = detailQ.data;
    setCustomerId(inv.customerId);
    setInvoiceDate(toDateInput(inv.invoiceDate));
    setTermsCode(inv.termsCode ?? '');
    setReference(inv.reference ?? '');
    setMemo(inv.memo ?? '');
    setDiscount(trimMoney(inv.discount) === '0' ? '' : trimMoney(inv.discount));
    setDeductsInventory(inv.deductsInventory);
    setBranchId(inv.branchId ?? '');
    setLines(
      rows.map((r) =>
        r.productId
          ? {
              key: nextKey(),
              kind: 'product',
              productId: r.productId,
              variantId: r.variantId,
              label: r.description || 'Product',
              qty: String(r.qty),
              unitPrice: trimMoney(r.unitPrice),
              taxCode: r.taxCode ?? '',
              incomeAccountCode: r.incomeAccountCode ?? '',
            }
          : {
              key: nextKey(),
              kind: 'freetext',
              description: r.description ?? '',
              qty: String(r.qty),
              unitPrice: trimMoney(r.unitPrice),
              taxCode: r.taxCode ?? '',
              incomeAccountCode: r.incomeAccountCode ?? '',
            },
      ),
    );
  }, [detailQ.data]);

  const subtotal = useMemo(() => lines.reduce((s, l) => s + lineAmount(l), 0), [lines]);
  const discountNum = Number(sanitizeMoney(discount)) || 0;
  const total = Math.max(subtotal - discountNum, 0);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['ar-invoices'] });
    void qc.invalidateQueries({ queryKey: ['ar-invoice', invoiceId] });
  };

  const body = () => ({
    invoiceDate: new Date(`${invoiceDate}T00:00:00`).toISOString(),
    termsCode: termsCode || null,
    reference: reference.trim() || null,
    memo: memo.trim() || null,
    discount: discountNum > 0 ? sanitizeMoney(discount) : null,
    deductsInventory,
    branchId: deductsInventory ? branchId || null : null,
    lines: lines.map(toInput),
  });

  const save = useMutation({
    mutationFn: async () =>
      invoiceId ? updateArInvoice(invoiceId, body()) : createArInvoice({ customerId, ...body() }),
    onSuccess: () => {
      toast.success('Draft saved.');
      invalidate();
      onClose();
    },
    onError: (err) => toast.error(friendly(err, 'Could not save the draft.')),
  });

  const post = useMutation({
    mutationFn: () => postArInvoice(invoiceId!),
    onSuccess: (res) => {
      // A warn-mode credit limit does NOT block the post — it reports.
      if (res.warnings.includes('credit_limit_exceeded')) {
        toast.info('Posted — this customer is now past their credit limit.', 9000);
      } else {
        toast.success('Billing posted.');
      }
      invalidate();
      onClose();
    },
    onError: (err) => toast.error(friendly(err, 'Could not post this billing.')),
  });

  const del = useMutation({
    mutationFn: () => deleteArInvoiceDraft(invoiceId!),
    onSuccess: () => {
      toast.success('Draft deleted.');
      invalidate();
      onClose();
    },
    onError: (err) => toast.error(friendly(err, 'Could not delete the draft.')),
  });

  const doVoid = useMutation({
    mutationFn: () => voidArInvoice(invoiceId!, voidReason.trim()),
    onSuccess: () => {
      toast.success('Billing voided.');
      invalidate();
      onClose();
    },
    onError: (err) => toast.error(friendly(err, 'Could not void this billing.')),
  });

  const busy = save.isPending || post.isPending || del.isPending || doVoid.isPending;
  const canSave = canWrite && customerId && lines.length > 0 && !busy;

  type LinePatch = Partial<Omit<ProductLine, 'kind' | 'key'> & Omit<FreeTextLine, 'kind' | 'key'>>;
  const setLine = (key: string, patch: LinePatch) =>
    setLines((prev) => prev.map((l) => (l.key === key ? ({ ...l, ...patch } as EditorLine) : l)));

  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {invoice ? `Billing ${invoice.docNo}` : 'New billing'}
            {invoice ? (
              <Badge
                variant={isVoid ? 'muted' : invoice.status === 'draft' ? 'warning' : 'success'}
              >
                {invoice.status}
              </Badge>
            ) : null}
          </DialogTitle>
        </DialogHeader>

        {detailQ.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Customer</label>
                <select
                  className={`${SELECT_CLS} w-full`}
                  value={customerId}
                  disabled={Boolean(invoiceId)}
                  onChange={(e) => setCustomerId(e.target.value)}
                >
                  <option value="">Select a customer…</option>
                  {(customersQ.data?.rows ?? []).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                {invoiceId ? (
                  <p className="text-xs text-muted-foreground">
                    Billing someone else is a different document — create a new one.
                  </p>
                ) : null}
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Billing date</label>
                <Input
                  type="date"
                  className="h-9"
                  value={invoiceDate}
                  disabled={!isDraft}
                  onChange={(e) => setInvoiceDate(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Terms</label>
                <select
                  className={`${SELECT_CLS} w-full`}
                  value={termsCode}
                  disabled={!isDraft}
                  onChange={(e) => setTermsCode(e.target.value)}
                >
                  <option value="">Use the customer&apos;s terms</option>
                  {(termsQ.data ?? []).map((t) => (
                    <option key={t.code} value={t.code}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Reference</label>
                <Input
                  className="h-9"
                  value={reference}
                  disabled={!isDraft}
                  placeholder="Their PO number, say"
                  onChange={(e) => setReference(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2 rounded-md border p-3">
              <label className="flex items-start gap-2 text-sm">
                <Checkbox
                  className="mt-0.5"
                  checked={deductsInventory}
                  disabled={!isDraft}
                  onCheckedChange={(v) => setDeductsInventory(v === true)}
                />
                <span>
                  Relieve stock for this billing
                  <span className="block text-xs text-muted-foreground">
                    Off by default — a billing is a request for payment, not a delivery. Turn it on
                    only when the goods leave against this document.
                  </span>
                </span>
              </label>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Branch</label>
                <select
                  className={`${SELECT_CLS} w-full`}
                  value={branchId}
                  disabled={!isDraft}
                  onChange={(e) => setBranchId(e.target.value)}
                >
                  <option value="">No branch</option>
                  {(branchesQ.data ?? []).map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground">
                  Where stock is relieved from, and whose address and TIN print on the statement.
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Lines</span>
                {isDraft && canWrite ? (
                  <div className="flex gap-2">
                    <div className="w-64">
                      <ProductPicker
                        selected={null}
                        placeholder="Add a product…"
                        onChange={(p) => {
                          if (!p) return;
                          // The picker stays empty: choosing appends a line
                          // rather than selecting one, so it can add several.
                          setLines((prev) => [
                            ...prev,
                            {
                              key: nextKey(),
                              kind: 'product',
                              productId: p.id,
                              variantId: null,
                              label: p.name,
                              qty: '1',
                              unitPrice: '',
                              taxCode: '',
                              incomeAccountCode: '',
                            },
                          ]);
                        }}
                      />
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        setLines((prev) => [
                          ...prev,
                          {
                            key: nextKey(),
                            kind: 'freetext',
                            description: '',
                            qty: '1',
                            unitPrice: '',
                            taxCode: '',
                            incomeAccountCode: '',
                          },
                        ])
                      }
                    >
                      Add charge
                    </Button>
                  </div>
                ) : null}
              </div>

              {lines.length === 0 ? (
                <p className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">
                  No lines yet. Add a product or a free-text charge.
                </p>
              ) : (
                <div className="space-y-2">
                  {lines.map((l) => (
                    <div key={l.key} className="grid gap-2 rounded-md border p-2 sm:grid-cols-12">
                      <div className="sm:col-span-4">
                        {l.kind === 'product' ? (
                          <div className="truncate pt-2 text-sm">{l.label}</div>
                        ) : (
                          <Input
                            className="h-9"
                            placeholder="Description"
                            value={l.description}
                            disabled={!isDraft}
                            onChange={(e) => setLine(l.key, { description: e.target.value })}
                          />
                        )}
                      </div>
                      <Input
                        className="h-9 sm:col-span-1"
                        type="number"
                        min={1}
                        value={l.qty}
                        disabled={!isDraft}
                        onChange={(e) => setLine(l.key, { qty: e.target.value })}
                      />
                      <MoneyInput
                        className="h-9 sm:col-span-2"
                        value={l.unitPrice}
                        disabled={!isDraft}
                        onChange={(e) => setLine(l.key, { unitPrice: e.target.value })}
                      />
                      <select
                        className={`${SELECT_CLS} sm:col-span-2`}
                        value={l.taxCode}
                        disabled={!isDraft}
                        onChange={(e) => setLine(l.key, { taxCode: e.target.value })}
                      >
                        <option value="">Default tax</option>
                        {TAX_CODES.map((t) => (
                          <option key={t} value={t}>
                            {TAX_LABELS[t] ?? t}
                          </option>
                        ))}
                      </select>
                      <select
                        className={`${SELECT_CLS} sm:col-span-2`}
                        value={l.incomeAccountCode}
                        disabled={!isDraft}
                        onChange={(e) => setLine(l.key, { incomeAccountCode: e.target.value })}
                      >
                        <option value="">Default income (4040)</option>
                        {incomeAccounts.map((a) => (
                          <option key={a.code} value={a.code}>
                            {a.code} — {a.name}
                          </option>
                        ))}
                      </select>
                      <div className="flex items-center justify-end gap-2 sm:col-span-1">
                        <span className="text-sm tabular-nums">{peso(lineAmount(l))}</span>
                        {isDraft && canWrite ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setLines((prev) => prev.filter((x) => x.key !== l.key))}
                          >
                            ×
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Memo</label>
                <Textarea
                  rows={3}
                  value={memo}
                  disabled={!isDraft}
                  onChange={(e) => setMemo(e.target.value)}
                />
              </div>
              <div className="space-y-2 rounded-md border p-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="tabular-nums">{peso(subtotal)}</span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-muted-foreground">Discount</span>
                  <MoneyInput
                    className="h-8 w-28 text-right"
                    value={discount}
                    disabled={!isDraft}
                    onChange={(e) => setDiscount(e.target.value)}
                  />
                </div>
                <div className="flex justify-between border-t pt-2 font-medium">
                  <span>Total</span>
                  <span className="tabular-nums">{peso(total)}</span>
                </div>
                {invoice && !isDraft ? (
                  <p className="text-xs text-muted-foreground">
                    VAT {peso(invoice.vatAmount)} · due {shortDate(invoice.dueDate)}. These figures
                    were frozen when the billing was posted.
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    VAT is computed from each line&apos;s tax code when you post.
                  </p>
                )}
              </div>
            </div>

            {voiding ? (
              <div className="space-y-2 rounded-md border border-destructive/40 p-3">
                <label className="text-xs text-muted-foreground">Reason for voiding</label>
                <Input
                  className="h-9"
                  value={voidReason}
                  autoFocus
                  onChange={(e) => setVoidReason(e.target.value)}
                />
                <div className="flex justify-end gap-2">
                  <Button size="sm" variant="ghost" onClick={() => setVoiding(false)}>
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    disabled={voidReason.trim().length < 3 || busy}
                    onClick={() => doVoid.mutate()}
                  >
                    Void billing
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        )}

        <DialogFooter className="flex-wrap gap-2">
          {invoiceId ? (
            <Button
              variant="outline"
              onClick={() => window.open(`/accounting/ar/${invoiceId}/print`, '_blank')}
            >
              Print
            </Button>
          ) : null}
          <div className="flex-1" />
          {isDraft && invoiceId && canWrite ? (
            <Button variant="ghost" disabled={busy} onClick={() => del.mutate()}>
              Delete draft
            </Button>
          ) : null}
          {!isDraft && !isVoid && canVoid ? (
            <Button variant="ghost" disabled={busy} onClick={() => setVoiding(true)}>
              Void
            </Button>
          ) : null}
          <Button variant="outline" disabled={busy} onClick={onClose}>
            Close
          </Button>
          {isDraft && canWrite ? (
            <Button disabled={!canSave} onClick={() => save.mutate()}>
              {save.isPending ? 'Saving…' : 'Save draft'}
            </Button>
          ) : null}
          {isDraft && invoiceId && canWrite ? (
            <Button disabled={busy || lines.length === 0} onClick={() => post.mutate()}>
              {post.isPending ? 'Posting…' : 'Post'}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
